/**
 * F-186: when the install / notification prompts may show. David: insistent,
 * with no "don't ask again". "Not now" hides the bar only until the next
 * session: a new login, or the app opened again after 30 minutes without use.
 * Kept in this browser only (localStorage), per origin.
 */

const KEY = 'memberfy:pushPrompt'
const FRESH_LOGIN_KEY = 'memberfy:pushPrompt:freshLogin'
/** A gap this long without use starts a new session. */
export const SESSION_GAP_MS = 30 * 60 * 1000

interface Stored {
  snoozed: boolean
  lastSeen: number
  /** The person removed this device by hand: no silent re-subscribe until they enable it again. */
  optedOut?: boolean
}

const read = (): Stored => {
  try {
    const parsed = JSON.parse(localStorage.getItem(KEY) || 'null') as Partial<Stored> | null
    return { snoozed: Boolean(parsed?.snoozed), lastSeen: Number(parsed?.lastSeen) || 0, optedOut: Boolean(parsed?.optedOut) }
  } catch {
    return { snoozed: false, lastSeen: 0 }
  }
}

const write = (next: Stored) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(next))
  } catch {
    /* Storage unavailable: the prompt simply comes back on the next load. */
  }
}

/** Called once on load: a long gap since the last use is a new session, and the snooze goes. */
export const startSession = (now = Date.now()) => {
  const stored = read()
  const fresh = now - stored.lastSeen > SESSION_GAP_MS
  write({ ...stored, snoozed: fresh ? false : stored.snoozed, lastSeen: now })
}

/** Keeps the session alive while the app is in use. */
export const touchSession = (now = Date.now()) => {
  write({ ...read(), lastSeen: now })
}

export const isSnoozed = (): boolean => read().snoozed

export const snooze = () => {
  write({ ...read(), snoozed: true, lastSeen: Date.now() })
}

export const isOptedOut = (): boolean => Boolean(read().optedOut)

export const setOptedOut = (value: boolean) => {
  write({ ...read(), optedOut: value })
}

/** Login (or sign-up) in this tab: a new session, and the modal is due once. */
export const markFreshLogin = () => {
  write({ ...read(), snoozed: false, lastSeen: Date.now() })
  try {
    sessionStorage.setItem(FRESH_LOGIN_KEY, '1')
  } catch {
    /* No session storage: no modal, the bar still shows. */
  }
}

export const isFreshLogin = (): boolean => {
  try {
    return sessionStorage.getItem(FRESH_LOGIN_KEY) === '1'
  } catch {
    return false
  }
}

export const clearFreshLogin = () => {
  try {
    sessionStorage.removeItem(FRESH_LOGIN_KEY)
  } catch {
    /* Nothing to clear. */
  }
}
